/**
 * Create / Edit a project material request.
 *
 * Fields:
 *   - Title (auto-suggested as "Request #ABCD")
 *   - Items[] — repeatable line items. Each item: name, brand,
 *     variety, make, size, unit, quantity, rate. Total computed.
 *   - "Pick from library" button per item → opens the library
 *     picker which prefills the row from a saved entry.
 *
 * Writes to `materialRequests/{id}` via
 *   - `createMaterialRequest` (create — auto-approve for Admin/Manager)
 *   - `updateMaterialRequest` (edit pending/draft)
 *   - `resubmitRejectedRequest` (resubmit after rejection)
 */
import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import { MaterialLibraryPickerSlideOver } from '@/components/MaterialLibraryPickerSlideOver';
import {
  UNIT_OPTIONS,
  getCategoryConfig,
} from '@/lib/materialLibrary';
import {
  createMaterialRequest,
  emptyItem,
  itemFromLibrary,
  resubmitRejectedRequest,
  updateMaterialRequest,
  type MaterialRequest,
  type MaterialRequestItem,
} from '@/lib/materialRequests';
import type { RoleKey } from '@/lib/useProjectMembers';

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};
const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--text-tertiary)',
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
};

export function MaterialRequestFormSlideOver({
  open,
  onClose,
  orgId,
  projectId,
  request,
  /** Caller's role on the org — drives auto-approve gate on create.
   *  Defaults to 'viewer' (no auto-approve) when caller doesn't
   *  know it. */
  creatorRole = 'viewer',
  /** True if this form should resubmit a previously rejected request
   *  instead of plain editing it. */
  resubmit = false,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  projectId: string;
  request: MaterialRequest | null;
  creatorRole?: RoleKey;
  resubmit?: boolean;
}) {
  const { user } = useAuth();
  const isEdit = Boolean(request);

  const [title, setTitle] = useState('');
  const [items, setItems] = useState<MaterialRequestItem[]>([emptyItem()]);
  const [pickerForIndex, setPickerForIndex] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (request) {
      setTitle(request.title);
      setItems(
        request.items.length > 0
          ? request.items.map((i) => ({ ...i }))
          : [emptyItem()],
      );
    } else {
      setTitle('');
      setItems([emptyItem()]);
    }
    setError(null);
    setPickerForIndex(null);
  }, [open, request]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pickerForIndex === null && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose, pickerForIndex]);

  const total = useMemo(
    () => items.reduce((s, i) => s + (i.totalCost || 0), 0),
    [items],
  );

  const patchItem = (i: number, patch: Partial<MaterialRequestItem>) => {
    setItems((prev) => {
      const next = [...prev];
      const current = { ...next[i], ...patch };
      // Always recompute totalCost when qty / rate change.
      current.totalCost =
        Number(current.quantity || 0) * Number(current.rate || 0);
      next[i] = current;
      return next;
    });
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (i: number) =>
    setItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, j) => j !== i)));

  const handleSubmit = async () => {
    setError(null);
    if (!orgId || !user) return setError('Sign-in required.');
    const validItems = items.filter(
      (it) => it.name.trim() && Number(it.quantity) > 0,
    );
    if (validItems.length === 0)
      return setError('Add at least one item with a name and quantity.');
    setBusy(true);
    try {
      if (isEdit && request && resubmit) {
        await resubmitRejectedRequest({
          requestId: request.id,
          title: title.trim(),
          items: validItems,
          editedBy: user.uid,
        });
      } else if (isEdit && request) {
        await updateMaterialRequest({
          requestId: request.id,
          title: title.trim(),
          items: validItems,
          editedBy: user.uid,
        });
      } else {
        await createMaterialRequest({
          orgId,
          projectId,
          title: title.trim(),
          items: validItems,
          createdBy: user.uid,
          creatorRole,
        });
      }
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={() => !busy && onClose()}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-list-checks" />{' '}
            {isEdit
              ? resubmit
                ? 'Resubmit Request'
                : 'Edit Request'
              : 'New Request'}
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {error ? (
            <div
              style={{
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}

          {/* Title */}
          <div>
            <label style={LABEL}>Title</label>
            <input
              style={{ ...INPUT, padding: '10px 12px', fontSize: 14 }}
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
              placeholder="e.g. Week 12 cement order"
              autoFocus
            />
          </div>

          {/* Items */}
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <label style={LABEL}>Items · {items.length}</label>
              <button
                type="button"
                onClick={addItem}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--brand)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <i className="ph-bold ph-plus" /> Add item
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {items.map((it, i) => (
                <ItemCard
                  key={i}
                  index={i}
                  item={it}
                  onChange={(p) => patchItem(i, p)}
                  onRemove={() => removeItem(i)}
                  onPickFromLibrary={() => setPickerForIndex(i)}
                  removable={items.length > 1}
                />
              ))}
            </div>
          </div>

          {/* Total */}
          <div
            style={{
              padding: 12,
              border: '1px solid var(--border)',
              borderRadius: 8,
              background: '#FAFAFA',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Total value
            </span>
            <span style={{ fontSize: 17, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              ₹{Math.round(total).toLocaleString('en-IN')}
            </span>
          </div>

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              marginTop: 8,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={busy}
            >
              {busy
                ? 'Saving…'
                : isEdit
                  ? resubmit
                    ? 'Resubmit for approval'
                    : 'Save changes'
                  : 'Create request'}
            </button>
          </div>
        </div>
      </div>

      {/* Library picker — fills the row at pickerForIndex on pick */}
      <MaterialLibraryPickerSlideOver
        open={pickerForIndex !== null}
        onClose={() => setPickerForIndex(null)}
        orgId={orgId}
        onPick={(libItem) => {
          if (pickerForIndex !== null) {
            setItems((prev) => {
              const next = [...prev];
              const qty = next[pickerForIndex].quantity || 1;
              next[pickerForIndex] = itemFromLibrary(libItem, qty);
              return next;
            });
          }
          setPickerForIndex(null);
        }}
      />
    </>
  );
}

// ───── Per-item card ─────

function ItemCard({
  index,
  item,
  onChange,
  onRemove,
  onPickFromLibrary,
  removable,
}: {
  index: number;
  item: MaterialRequestItem;
  onChange: (patch: Partial<MaterialRequestItem>) => void;
  onRemove: () => void;
  onPickFromLibrary: () => void;
  removable: boolean;
}) {
  const cfg = item.category ? getCategoryConfig(item.category) : null;
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        background: '#fff',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          Item #{index + 1}
          {cfg ? (
            <span
              style={{
                background: cfg.tintBg,
                color: cfg.color,
                padding: '2px 6px',
                borderRadius: 4,
                fontSize: 10,
              }}
            >
              <i className={cfg.icon} /> {cfg.label}
            </span>
          ) : null}
          {item.libraryItemId ? (
            <span
              style={{
                background: 'var(--brand-bg)',
                color: 'var(--brand)',
                padding: '2px 6px',
                borderRadius: 4,
                fontSize: 9,
              }}
            >
              FROM LIBRARY
            </span>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            onClick={onPickFromLibrary}
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--brand)',
              background: 'none',
              border: '1px solid var(--brand)',
              padding: '3px 8px',
              borderRadius: 4,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <i className="ph ph-books" /> Pick from library
          </button>
          {removable ? (
            <button
              type="button"
              onClick={onRemove}
              aria-label="Remove item"
              style={{
                fontSize: 14,
                color: 'var(--critical)',
                background: 'none',
                border: '1px solid var(--border)',
                padding: '3px 8px',
                borderRadius: 4,
                cursor: 'pointer',
              }}
              title="Remove item"
            >
              <i className="ph ph-trash" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Name */}
      <div>
        <label style={LABEL}>Name *</label>
        <input
          style={INPUT}
          value={item.name}
          onChange={(e) => onChange({ name: e.currentTarget.value })}
          placeholder="e.g. 18mm BWP Plywood, Greenply"
        />
      </div>

      {/* Brand / variety / make / size — compact 2-col grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label style={LABEL}>Brand</label>
          <input
            style={INPUT}
            value={item.brand}
            onChange={(e) => onChange({ brand: e.currentTarget.value })}
            placeholder="Brand"
          />
        </div>
        <div>
          <label style={LABEL}>Variety</label>
          <input
            style={INPUT}
            value={item.variety}
            onChange={(e) => onChange({ variety: e.currentTarget.value })}
            placeholder="Type / Code"
          />
        </div>
        <div>
          <label style={LABEL}>Make / Finish</label>
          <input
            style={INPUT}
            value={item.make}
            onChange={(e) => onChange({ make: e.currentTarget.value })}
            placeholder="Make / Finish"
          />
        </div>
        <div>
          <label style={LABEL}>Size</label>
          <input
            style={INPUT}
            value={item.size}
            onChange={(e) => onChange({ size: e.currentTarget.value })}
            placeholder="Size"
          />
        </div>
      </div>

      {/* Qty / unit / rate / total — 4-col compact */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 8,
        }}
      >
        <div>
          <label style={LABEL}>Qty *</label>
          <input
            style={INPUT}
            value={item.quantity || ''}
            inputMode="decimal"
            onChange={(e) =>
              onChange({
                quantity: Number(e.currentTarget.value.replace(/[^\d.]/g, '')) || 0,
              })
            }
            placeholder="0"
          />
        </div>
        <div>
          <label style={LABEL}>Unit</label>
          <select
            style={{ ...INPUT, padding: '7px 8px', height: 34 }}
            value={item.unit}
            onChange={(e) => onChange({ unit: e.currentTarget.value })}
          >
            {UNIT_OPTIONS.map((u) => (
              <option key={u.key} value={u.key}>
                {u.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={LABEL}>Rate (₹)</label>
          <input
            style={INPUT}
            value={item.rate || ''}
            inputMode="decimal"
            onChange={(e) =>
              onChange({
                rate: Number(e.currentTarget.value.replace(/[^\d.]/g, '')) || 0,
              })
            }
            placeholder="0"
          />
        </div>
        <div>
          <label style={LABEL}>Total (₹)</label>
          <div
            style={{
              ...INPUT,
              background: '#FAFAFA',
              cursor: 'not-allowed',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--text-primary)',
            }}
          >
            {Math.round(item.totalCost || 0).toLocaleString('en-IN')}
          </div>
        </div>
      </div>
    </div>
  );
}
